import { useEffect, useRef, useCallback } from 'react'
import { useApiCallManager } from '../utils/apiCallManager'

/**
 * React hook for managing API calls with proper cleanup and cancellation
 * Prevents duplicate calls and handles component unmounting
 */
export const useApiManager = () => {
  const { executeRequest, debounceRequest, cancelRequest, cancelAllRequests } = useApiCallManager()
  const activeRequestsRef = useRef(new Set())

  // Track active requests for this component
  const trackRequest = useCallback((requestKey) => {
    activeRequestsRef.current.add(requestKey)
  }, [])

  const untrackRequest = useCallback((requestKey) => {
    activeRequestsRef.current.delete(requestKey)
  }, [])

  // Execute API request with component-level tracking
  const executeApiRequest = useCallback(async (requestKey, apiCall, options = {}) => {
    trackRequest(requestKey)
    
    try {
      const result = await executeRequest(requestKey, apiCall, {
        ...options,
        onComplete: (response) => {
          untrackRequest(requestKey)
          if (options.onComplete) options.onComplete(response)
        },
        onError: (error) => {
          untrackRequest(requestKey)
          if (options.onError) options.onError(error)
        }
      })
      return result
    } catch (error) {
      untrackRequest(requestKey)
      throw error
    }
  }, [executeRequest, trackRequest, untrackRequest])

  // Execute debounced API request with component-level tracking
  const executeDebounceRequest = useCallback(async (requestKey, apiCall, delay = 300, options = {}) => {
    trackRequest(requestKey)
    
    try {
      const result = await debounceRequest(requestKey, apiCall, delay, {
        ...options,
        onComplete: (response) => {
          untrackRequest(requestKey)
          if (options.onComplete) options.onComplete(response)
        },
        onError: (error) => {
          untrackRequest(requestKey)
          if (options.onError) options.onError(error)
        }
      })
      return result
    } catch (error) {
      untrackRequest(requestKey)
      throw error
    }
  }, [debounceRequest, trackRequest, untrackRequest])

  // Cancel specific request
  const cancelApiRequest = useCallback((requestKey) => {
    cancelRequest(requestKey)
    untrackRequest(requestKey)
  }, [cancelRequest, untrackRequest])

  // Cancel all requests for this component
  const cancelAllApiRequests = useCallback(() => {
    activeRequestsRef.current.forEach(requestKey => {
      cancelRequest(requestKey)
    })
    activeRequestsRef.current.clear()
  }, [cancelRequest])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // Cancel all active requests when component unmounts
      cancelAllApiRequests()
    }
  }, [cancelAllApiRequests])

  return {
    executeApiRequest,
    executeDebounceRequest,
    cancelApiRequest,
    cancelAllApiRequests,
    getActiveRequestCount: () => activeRequestsRef.current.size
  }
}

/**
 * Hook for managing data fetching with dependencies
 * Automatically cancels previous requests when dependencies change
 */
export const useDataFetcher = (fetchFunction, dependencies = [], options = {}) => {
  const { executeApiRequest, cancelAllApiRequests } = useApiManager()
  const previousDepsRef = useRef()
  const { 
    enabled = true, 
    onSuccess = null, 
    onError = null,
    requestKey = null 
  } = options

  useEffect(() => {
    // Skip if disabled
    if (!enabled) return

    // Check if dependencies have changed
    const depsChanged = !previousDepsRef.current || 
      dependencies.some((dep, index) => dep !== previousDepsRef.current[index])

    if (depsChanged && dependencies.every(dep => dep != null)) {
      // Cancel previous requests
      cancelAllApiRequests()
      
      // Generate request key if not provided
      const key = requestKey || `dataFetcher:${JSON.stringify(dependencies)}`
      
      // Execute the fetch function
      executeApiRequest(
        key,
        fetchFunction,
        {
          onComplete: onSuccess,
          onError: onError
        }
      ).catch(error => {
        if (error.message !== 'Request cancelled' && onError) {
          onError(error)
        }
      })
      
      // Update previous dependencies
      previousDepsRef.current = [...dependencies]
    }
  }, [fetchFunction, enabled, onSuccess, onError, requestKey, executeApiRequest, cancelAllApiRequests, ...dependencies])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cancelAllApiRequests()
    }
  }, [cancelAllApiRequests])
}

/**
 * Hook for managing search with debouncing
 */
export const useSearchManager = (searchFunction, delay = 300) => {
  const { executeDebounceRequest, cancelApiRequest } = useApiManager()
  const currentSearchRef = useRef(null)

  const search = useCallback(async (searchTerm, options = {}) => {
    // Cancel previous search
    if (currentSearchRef.current) {
      cancelApiRequest(currentSearchRef.current)
    }

    if (!searchTerm || searchTerm.trim().length < 3) {
      return
    }

    const requestKey = `search:${searchTerm.trim()}`
    currentSearchRef.current = requestKey

    try {
      const result = await executeDebounceRequest(
        requestKey,
        (signal) => searchFunction(searchTerm.trim(), signal),
        delay,
        options
      )
      
      // Clear current search if this was the latest one
      if (currentSearchRef.current === requestKey) {
        currentSearchRef.current = null
      }
      
      return result
    } catch (error) {
      // Clear current search on error
      if (currentSearchRef.current === requestKey) {
        currentSearchRef.current = null
      }
      throw error
    }
  }, [searchFunction, delay, executeDebounceRequest, cancelApiRequest])

  const cancelCurrentSearch = useCallback(() => {
    if (currentSearchRef.current) {
      cancelApiRequest(currentSearchRef.current)
      currentSearchRef.current = null
    }
  }, [cancelApiRequest])

  return {
    search,
    cancelCurrentSearch,
    isSearching: () => currentSearchRef.current !== null
  }
}